import {
  BlotterTable,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  Portal,
  Toggle,
  VStack,
  columnTypes,
  getShowJSONContextItem,
  isGridApiReady,
  prettifyLedgerUpdateTypeEnum,
  selectAll,
  useBlotterTable,
  useDynamicCallback,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  type Column,
  type ColumnDef,
  type LedgerEvent,
  type SubscriptionResponse,
  type UseBlotterTableProps,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, SelectionChangedEvent } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Observable } from 'rxjs';
import { LedgerEventsBlotterSummaryLine } from '../../../Blotters/LedgerEvents/LedgerEventsBlotterSummaryLine';
import { useReconDetailsInteractions } from './ReconDetailsInteractionsProvider';

interface ReconLedgerEventsBlotterProps {
  ledgerEventsObs: Observable<SubscriptionResponse<LedgerEvent>>;
  errorObservable: UseBlotterTableProps<unknown>['errorObservable'];
}

export const GROUP_BY_EVENTS_TOGGLE_PORTAL_ID = 'GroupByEventsTogglePortalID';
const ROW_COUNT_COLUMN_ID = 'row-count-col-id';

export const ReconLedgerEventsBlotter = ({ ledgerEventsObs, errorObservable }: ReconLedgerEventsBlotterProps) => {
  const mixpanel = useMixpanel();
  const [groupEvents, setGroupEvents] = useState(false); // always starts false

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const { jsonModal, handleClickJson } = useJsonModal();
  const getContextMenuItems = useDynamicCallback((params: GetContextMenuItemsParams<LedgerEvent>) => {
    const data = params.node?.data;
    return compact([
      selectAll(params, mixpanel),
      'separator',
      data ? getShowJSONContextItem({ params, handleClickJson }) : undefined,
      'separator',
      ...getDefaultContextMenuItems(params),
    ]);
  });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const [selectedRows, setSelectedRows] = useState<LedgerEvent[]>([]);

  const handleSelectionChanged = useCallback(({ api }: SelectionChangedEvent<LedgerEvent>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const rows = api.getSelectedRows();
    setSelectedRows(rows);
  }, []);

  const blotterTable = useBlotterTable<LedgerEvent>({
    dataObservable: ledgerEventsObs,
    errorObservable,
    columns,
    rowID: 'rowID' satisfies keyof LedgerEvent,
    gridOptions: {
      onSelectionChanged: handleSelectionChanged,
      groupRemoveSingleChildren: true,
      showOpenedGroup: true,
      autoGroupColumnDef,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems,
    },
  });

  const { setRowGroupColumns, setColumnsVisible, expandAllGroups, collapseAllGroups, highlightRows, selectRows } =
    blotterTable;

  // Register the utility function with the interactions provider
  const { setHighlightRows } = useReconDetailsInteractions();
  useEffect(() => {
    setHighlightRows(() => (ledgerEvents: LedgerEvent[]) => {
      const rowIDs = ledgerEvents.map(le => le.rowID);
      highlightRows(rowIDs);
      selectRows(rowIDs);
    });
  }, [setHighlightRows, highlightRows, selectRows]);

  const handleGroupByEventTypeChanged = useDynamicCallback((checked: boolean) => {
    setRowGroupColumns(checked ? ['Type' satisfies keyof LedgerEvent] : []);
    setColumnsVisible([ROW_COUNT_COLUMN_ID], checked);
    setGroupEvents(checked);
  });

  return (
    <VStack h="100%">
      <BlotterTable {...blotterTable} background="colors.gray['020']" />
      <LedgerEventsBlotterSummaryLine rows={selectedRows} />
      <Portal portalId={GROUP_BY_EVENTS_TOGGLE_PORTAL_ID}>
        <HStack gap="spacing4">
          {groupEvents && (
            <>
              <IconButton
                icon={IconName.ListExpand}
                size={FormControlSizes.Small}
                variant={ButtonVariants.Default}
                onClick={expandAllGroups}
              />
              <IconButton
                icon={IconName.ListCollapse}
                size={FormControlSizes.Small}
                variant={ButtonVariants.Default}
                onClick={collapseAllGroups}
              />
              <Divider orientation="vertical" />
            </>
          )}
          <Toggle
            size={FormControlSizes.Small}
            label="Group by event type"
            checked={groupEvents}
            onChange={handleGroupByEventTypeChanged}
          />
        </HStack>
      </Portal>
      {jsonModal}
    </VStack>
  );
};

const columns: Column[] = [
  {
    type: 'text',
    field: 'Type',
    title: 'Talos Ledger Event',
    params: { getLabel: prettifyLedgerUpdateTypeEnum },
    initialRowGroup: false, // always starts false, user can enable
  },
  {
    type: 'rowCount',
    id: ROW_COUNT_COLUMN_ID,
    title: 'Total Events',
    aggregate: true,
    initialWidth: 120,
    initialHide: true, // always starts hidden, can be enabled
  },
  {
    type: 'number',
    field: 'Amount',
    params: {
      currencyField: 'amountAssetWithFallback' satisfies keyof LedgerEvent,
      highlightNegative: true,
      truncate: false,
    },
  },
  {
    type: 'date',
    field: 'TransactTime',
    title: 'Venue Transact Time',
    params: { milliseconds: true },
    initialSort: '+',
    initialSortIndex: 0,
  },
  { type: 'text', field: 'Revision', title: 'Ledger Revision', initialSort: '+', initialSortIndex: 1 },
  { type: 'date', field: 'Timestamp', title: 'Talos Received Time', params: { milliseconds: true } },
  {
    type: 'number',
    field: 'ResultingAmount',
    title: 'Balance',
    params: {
      currencyField: 'amountAssetWithFallback' satisfies keyof LedgerEvent,
      highlightNegative: true,
      truncate: false,
    },
    initialHide: true,
  },
  {
    type: 'text',
    field: 'TradeID',
    title: 'External ID',
  },
  {
    type: 'text',
    field: 'Comments',
  },
  {
    type: 'size',
    field: 'Fee',
    params: {
      currencyField: 'FeeCurrency' satisfies keyof LedgerEvent,
    },
    initialHide: true,
  },
  {
    type: 'price',
    field: 'AvgCost',
    params: {
      quoteCurrencyField: 'AvgCostCurrency' satisfies keyof LedgerEvent,
    },
  },
  {
    type: 'text',
    field: 'Asset',
    initialHide: true,
  },
  {
    type: 'text',
    field: 'AmountAsset',
    initialHide: true,
  },
] satisfies ColumnDef<LedgerEvent>[];
